import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Flame, Sparkles } from "lucide-react";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter190 — "You're back" modal. Fires ONCE for a rep whose streak
 * bridge was recently activated (they broke their streak, then closed
 * a fresh deal inside the recovery window). The bridge preserves the
 * pre-break streak length; this modal is the visual receipt.
 *
 * Contract:
 *   GET  /api/leaderboard/streak-bridge-celebration
 *        → { show: bool, prev_streak?, break_start_date?, activated_at? }
 *   POST /api/leaderboard/streak-bridge-celebration/ack
 *        → { ok: true, acknowledged: bool }
 *
 * Behaviour:
 *   - Mounted globally under AuthContext.
 *   - Fetches once per session on user mount + once when the browser
 *     regains focus (so an admin who fires the recovery scan while
 *     the rep already has WarMap open still sees it).
 *   - `Close` and `Nice — I'm back` both POST to /ack. Ack is idempotent
 *     so double-firing is harmless.
 */
export function YoureBackModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [checked, setChecked] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;

    const check = async () => {
      try {
        const r = await fetch(`${API}/leaderboard/streak-bridge-celebration`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        if (d?.show && Number(d.prev_streak) > 0) {
          setPayload(d);
          setOpen(true);
        }
      } catch { /* silent — nothing to celebrate */ }
      finally {
        if (!cancelled) setChecked(true);
      }
    };

    check();

    const onFocus = () => { if (!open) check(); };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.user_id]);

  const ack = async () => {
    setAcking(true);
    try {
      await fetch(`${API}/leaderboard/streak-bridge-celebration/ack`, {
        method: "POST",
        credentials: "include",
      });
    } catch { /* silent — server will refetch cleanly on next mount */ }
    finally {
      setAcking(false);
      setOpen(false);
    }
  };

  if (!checked || !payload) return null;

  const prevStreak = Number(payload.prev_streak) || 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) ack();
      }}
    >
      <DialogContent
        data-testid="youre-back-modal"
        className="bg-gradient-to-br from-[#0d1f16] via-[#0a0f0c] to-[#0a0a0a] border-emerald-500/30 text-white sm:max-w-md"
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-600 via-emerald-400 to-emerald-600 rounded-t-lg" />
        <DialogHeader className="items-center text-center pt-4">
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Flame className="w-8 h-8 text-emerald-400" data-testid="youre-back-flame" />
          </div>
          <DialogTitle className="text-2xl font-heading text-white flex items-center gap-2 justify-center">
            <Sparkles className="w-5 h-5 text-emerald-300" />
            You&apos;re back!
          </DialogTitle>
          <DialogDescription className="text-gray-300 text-base leading-relaxed pt-2">
            Your <span className="text-emerald-300 font-semibold" data-testid="youre-back-streak">{prevStreak}-day streak</span> has been preserved.
            <br />
            Today&apos;s close bridged the gap — momentum stays.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center pt-2">
          <Button
            data-testid="youre-back-dismiss"
            onClick={ack}
            disabled={acking}
            className="bg-emerald-600 hover:bg-emerald-500 text-white w-full sm:w-auto"
          >
            Nice — I&apos;m back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
